import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, FileText, Sparkles, RefreshCcw, ChevronRight, CheckCircle2 } from "lucide-react";
import AdminHeader from "@/components/AdminHeader";
import ReadinessPanel from "@/components/ReadinessPanel";
import { listBriefs, regenerateBrief, setBriefStatus, generateBrief, getAdminToken } from "@/lib/api";
import { toast } from "sonner";
import AdminGate from "@/components/AdminGate";

export default function AdminBriefs() {
  const [ready, setReady] = useState(!!getAdminToken());
  if (!ready) return <AdminGate onAuthed={() => setReady(true)} redirect="/admin/briefs" />;
  return <AdminBriefsInner />;
}

function AdminBriefsInner() {
  const navigate = useNavigate();
  const [briefs, setBriefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const list = await listBriefs();
      setBriefs(list);
    } catch (err) {
      toast.error("Couldn't load briefs.", { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runRegenerate = async (id) => {
    try {
      const res = await regenerateBrief(id);
      toast.success(`Regenerated as v${res.brief.version}`);
      load();
      navigate(`/admin/briefs/${res.brief.id}`);
    } catch (err) {
      toast.error("Regenerate failed.", { description: err?.response?.data?.detail || err.message });
    }
  };

  const runMarkReady = async (id) => {
    try {
      await setBriefStatus(id, "ready_for_review");
      toast.success("Marked ready for review");
      load();
    } catch (err) {
      toast.error("Status update failed.");
    }
  };

  const runGenerateNew = async () => {
    setGenBusy(true);
    try {
      const res = await generateBrief("demo-ridgeline");
      toast.success(`New draft generated (v${res.brief.version})`);
      load();
      navigate(`/admin/briefs/${res.brief.id}`);
    } catch (err) {
      toast.error("Generation failed.", { description: err?.response?.data?.detail || err.message });
    } finally {
      setGenBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--ta-bg)" }} data-testid="admin-briefs-page">
      <AdminHeader onRefresh={load} refreshing={busy} active="briefs" />

      <main className="w-full max-w-screen-lg mx-auto px-5 sm:px-8 py-8 page-fade">
        <ReadinessPanel />

        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
              Content engine
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1" style={{ color: "var(--ta-primary)" }}>
              Monday Briefs
            </h1>
            <p className="mt-2 text-sm max-w-lg" style={{ color: "var(--ta-text-secondary)" }}>
              Every brief is generated from observations. Regenerating creates a new version — earlier versions are preserved so you can compare.
            </p>
          </div>
          <button
            onClick={runGenerateNew}
            disabled={genBusy}
            data-testid="admin-generate-new"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: "var(--ta-primary)" }}
          >
            {genBusy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Generate new draft
          </button>
        </div>

        {briefs === null && (
          <div className="flex items-center gap-2 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
            <Loader2 size={16} className="animate-spin" /> Loading briefs…
          </div>
        )}

        {briefs && briefs.length === 0 && (
          <div
            className="p-10 rounded-3xl bg-white border text-center"
            style={{ borderColor: "var(--ta-border)" }}
            data-testid="admin-briefs-empty"
          >
            <div
              className="mx-auto w-14 h-14 rounded-2xl inline-flex items-center justify-center mb-3"
              style={{ background: "var(--ta-accent)", color: "var(--ta-primary)" }}
            >
              <FileText size={22} strokeWidth={2.2} />
            </div>
            <div className="font-display text-xl font-semibold" style={{ color: "var(--ta-primary)" }}>No briefs yet.</div>
            <p className="mt-1 text-sm" style={{ color: "var(--ta-text-secondary)" }}>Click "Generate new draft" to create the first one.</p>
          </div>
        )}

        {briefs && briefs.length > 0 && (
          <div className="flex flex-col gap-3" data-testid="admin-briefs-list">
            {briefs.map((b) => (
              <BriefRow
                key={b.id}
                b={b}
                onPreview={() => navigate(`/admin/briefs/${b.id}`)}
                onRegenerate={() => runRegenerate(b.id)}
                onMarkReady={() => runMarkReady(b.id)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const STATUS_COLOR = {
  draft: { fg: "var(--ta-primary)", bg: "var(--ta-accent)" },
  ready_for_review: { fg: "#8A5A00", bg: "#FFF3D6" },
  approved: { fg: "var(--ta-success)", bg: "var(--ta-success-bg)" },
  scheduled: { fg: "var(--ta-secondary)", bg: "#FCE6DE" },
  sent: { fg: "var(--ta-success)", bg: "var(--ta-success-bg)" },
  failed: { fg: "#B04635", bg: "#FDECEA" },
};
const STATUS_LABEL = {
  draft: "Draft",
  ready_for_review: "Ready for review",
  approved: "Approved",
  scheduled: "Scheduled",
  sent: "Sent",
  failed: "Failed",
};

function BriefRow({ b, onPreview, onRegenerate, onMarkReady }) {
  const c = STATUS_COLOR[b.status] || STATUS_COLOR.draft;
  const gen = new Date(b.generatedAt).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
  const weekStart = new Date(b.weekStartDate).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const weekEnd = new Date(b.weekEndDate).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  return (
    <div
      className="p-5 sm:p-6 rounded-2xl bg-white border"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid={`admin-brief-row-${b.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
            v{b.version} · Week of {weekStart}–{weekEnd}
          </div>
          <div className="font-display text-lg font-semibold mt-0.5 truncate" style={{ color: "var(--ta-primary)" }}>
            {b.businessName || b.businessId}
          </div>
          <div className="text-sm mt-1 truncate" style={{ color: "var(--ta-text-secondary)" }}>
            {b.subjectLine}
          </div>
        </div>
        <span
          className="text-[11px] uppercase tracking-widest font-semibold px-3 py-1.5 rounded-full shrink-0"
          style={{ background: c.bg, color: c.fg }}
          data-testid={`admin-brief-status-${b.id}`}
        >
          {STATUS_LABEL[b.status] || b.status}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-4 mb-4">
        <Stat label="Positive" value={b.positiveCount} />
        <Stat label="Attention" value={b.attentionCount} />
        <Stat label="Recommendations" value={b.recommendationCount} />
      </div>

      <div className="flex items-center justify-between text-xs mt-4" style={{ color: "var(--ta-text-secondary)" }}>
        <span>Generated {gen}</span>
        <div className="flex flex-wrap gap-2 justify-end">
          {b.status === "draft" && (
            <button
              onClick={onMarkReady}
              data-testid={`admin-brief-mark-ready-${b.id}`}
              className="inline-flex items-center gap-1 h-9 px-3 rounded-full text-xs font-medium border-2"
              style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)", background: "#fff" }}
            >
              <CheckCircle2 size={14} /> Mark ready
            </button>
          )}
          <button
            onClick={onRegenerate}
            data-testid={`admin-brief-regenerate-${b.id}`}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full text-xs font-medium border-2"
            style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)", background: "#fff" }}
          >
            <RefreshCcw size={14} /> Regenerate
          </button>
          <button
            onClick={onPreview}
            data-testid={`admin-brief-preview-${b.id}`}
            className="inline-flex items-center gap-1 h-9 px-4 rounded-full text-xs font-semibold text-white"
            style={{ background: "var(--ta-primary)" }}
          >
            Preview <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-xl p-3" style={{ background: "var(--ta-surface-alt)" }}>
    <div className="font-display text-2xl font-semibold tabular-nums leading-none" style={{ color: "var(--ta-primary)" }}>{value ?? 0}</div>
    <div className="text-xs mt-1" style={{ color: "var(--ta-text-secondary)" }}>{label}</div>
  </div>
);
